import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ProfileSmallSvg from "./svg/ProfileSmallSvg";
import "./RetailProfile.css";
import LockSvg from "./svg/LockSvg";
import { EditIcon } from "../../../../components/icons/Icons";
import GradientButton from "../../../../components/button/Button";
import axiosInstance from "../../../../axiosInstance";
import { useDispatch } from "react-redux";
import { logout } from "../../../../state/authSlice";
import LoadingComponent from "../../../../components/loading/Loading";

interface EditableProps {
  isEditable: boolean;
  personalDetails: PersonalDetails;
  setPersonalDetails: React.Dispatch<React.SetStateAction<PersonalDetails>>;
}

interface PersonalDetails {
  full_name: string;
  email: string;
  phone_number: string;
}

const PersonalInfo: React.FC<EditableProps> = ({
  isEditable,
  personalDetails,
  setPersonalDetails,
}) => {
  return (
    <div className="profile__flex__value">
      <h4>Full Name</h4>
      <input
        type="text"
        placeholder="Eg John Doe"
        readOnly={!isEditable}
        value={personalDetails.full_name}
        onChange={(e) =>
          setPersonalDetails({ ...personalDetails, full_name: e.target.value })
        }
      />
      <h4>Email Address</h4>
      <input
        type="email"
        placeholder="Eg Johndoe@gmail.com"
        readOnly
        value={personalDetails.email}
      />
      <h4>Phone number</h4>
      <input
        type="text"
        placeholder="+234 6178 88221"
        readOnly={!isEditable}
        value={personalDetails.phone_number}
        onChange={(e) =>
          setPersonalDetails({
            ...personalDetails,
            phone_number: e.target.value,
          })
        }
      />
    </div>
  );
};

const ChangePassword: React.FC<{ isEditable: boolean }> = ({ isEditable }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleChangePassword = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError("All fields are required.");
      setLoading(false);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      setLoading(false);
      return;
    }
    try {
      await axiosInstance.put("/auth/change-password", {
        currentPassword,
        newPassword,
      });
      setSuccess("Password changed successfully.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: any) {
      setError("Failed to change password.");
    }
    setLoading(false);
  };

  return (
    <div className="profile__flex__value">
      <h4>Current Password</h4>
      <input
        type="password"
        placeholder="Enter your current password"
        readOnly={!isEditable}
        value={currentPassword}
        onChange={(e) => setCurrentPassword(e.target.value)}
      />
      <h4>New Password</h4>
      <input
        type="password"
        placeholder="Enter your new password"
        readOnly={!isEditable}
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
      />
      <h4>Confirm Password</h4>
      <input
        type="password"
        placeholder="Re-enter your password"
        readOnly={!isEditable}
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
      />
      {isEditable && (
        <div style={{ marginTop: 16 }}>
          <GradientButton
            text={loading ? "Saving..." : "Save Password"}
            onClick={handleChangePassword}
            disabled={loading}
          />
          {success && (
            <div style={{ color: "green", marginTop: 8 }}>{success}</div>
          )}
          {error && (
            <div style={{ color: "#f44336", marginTop: 8 }}>{error}</div>
          )}
        </div>
      )}
    </div>
  );
};

const RetailProfile: React.FC = () => {
  const [activeTab, setActiveTab] = useState("personalInfo");
  const [isEditable, setIsEditable] = useState(false);
  const [personalDetails, setPersonalDetails] = useState<PersonalDetails>({
    full_name: "",
    email: "",
    phone_number: "",
  });
  const [originalPersonalDetails, setOriginalPersonalDetails] =
    useState<PersonalDetails>({
      full_name: "",
      email: "",
      phone_number: "",
    });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPersonalDetails = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get("/auth/me");
        setPersonalDetails(response.data.data);
        setOriginalPersonalDetails(response.data.data);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };
    fetchPersonalDetails();
  }, []);

  const dispatch = useDispatch();

  const handleLogout = () => {
    setLoading(true);
    dispatch(logout());

    // setTimeout(() => {
    //   window.location.href = "/login";
    // }, 3000);
  };

  if (loading) {
    return <LoadingComponent />;
  }

  const renderActiveTabContent = () => {
    switch (activeTab) {
      case "personalInfo":
        return (
          <PersonalInfo
            isEditable={isEditable}
            personalDetails={personalDetails}
            setPersonalDetails={setPersonalDetails}
          />
        );
      case "changePassword":
        return <ChangePassword isEditable={isEditable} />;
      default:
        return null;
    }
  };

  const renderActiveTabHeader = () => {
    switch (activeTab) {
      case "personalInfo":
        return "Personal Information";
      case "changePassword":
        return "Change Password";
      default:
        return "";
    }
  };

  const handleEditClick = () => {
    setIsEditable(!isEditable);
    setSuccess(null);
    setError(null);
  };

  const handleSave = async () => {
    if (!isEditable) return;
    const payload: any = {};
    Object.keys(personalDetails).forEach((key) => {
      if (
        personalDetails[key as keyof PersonalDetails] !==
        originalPersonalDetails[key as keyof PersonalDetails]
      ) {
        payload[key] = personalDetails[key as keyof PersonalDetails];
      }
    });
    if (Object.keys(payload).length === 0) {
      setError("No changes to save.");
      return;
    }
    try {
      setLoading(true);
      await axiosInstance.put("/users", payload);
      setOriginalPersonalDetails(personalDetails);
      setIsEditable(false);
      setSuccess("Profile updated successfully.");
      setError(null);
      setLoading(false);
    } catch (error) {
      setError("Failed to update profile.");
      setLoading(false);
    }
  };

  return (
    <div className="retail__profile">
      <div className="retail__profile_side">
        <h3>Profile</h3>

        <div className="retail__profile_side-tabs">
          <p className="head"> personal profile </p>
          <p
            className={`item ${activeTab === "personalInfo" ? "active" : ""}`}
            onClick={() => setActiveTab("personalInfo")}
          >
            <span>
              <ProfileSmallSvg />
            </span>
            <span>personal information</span>
          </p>
        </div>

        <div className="retail__profile_side-tabs">
          <p className="head"> security </p>
          <p
            className={`item ${activeTab === "changePassword" ? "active" : ""}`}
            onClick={() => setActiveTab("changePassword")}
          >
            <span>
              <LockSvg />
            </span>
            <span>change password</span>
          </p>
        </div>
      </div>

      <div className="retail__profile_main">
        <div
          className="retail__profile_main-header"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h3>{renderActiveTabHeader()}</h3>
          <div style={{ display: "flex", gap: 12 }}>
            <button
              onClick={handleEditClick}
              style={{
                border: "0.1px solid green",
                borderRadius: 6,
                padding: "0.5rem 1.2rem",
                fontWeight: 500,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              Edit
            </button>
            <button
              onClick={handleLogout}
              style={{
                background: "#f44336",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                padding: "0.5rem 1.2rem",
                fontWeight: 500,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              Logout
            </button>
          </div>
        </div>

        <div>{renderActiveTabContent()}</div>

        {isEditable && activeTab === "personalInfo" && (
          <div className="retail__profile-btn">
            <GradientButton
              text={loading ? "Saving..." : "Save Changes"}
              onClick={handleSave}
              disabled={loading}
            />
            {success && (
              <div style={{ color: "green", marginTop: 8 }}>{success}</div>
            )}
            {error && (
              <div style={{ color: "#f44336", marginTop: 8 }}>{error}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RetailProfile;
